/**
 * Validates platform-specific search fields for jobs with "all" and "one" fields
 * @param {object} searchJson - The searchJson object containing platform fields
 * @param {string} jobType - The job platform type
 * @returns {string|null} - Error message if validation fails, null if valid
 */
export const platformFieldsValidation = (searchJson, jobType) => {
    // Only validate platforms that have "all" field
    const platformsWithAllField = ['nf', 'nfo', 'ig', 'nftw', 'tt', 'tg', 'bsky'];
    
    if (!platformsWithAllField.includes(jobType)) {
        return null;
    }
    
    if (!searchJson) {
        return null;
    }
    
    const allTerms = countSearchTerms(searchJson.all || '');
    const oneTerms = countSearchTerms(searchJson.one || '');
    const totalTerms = allTerms + oneTerms;
    
    if (totalTerms > 30) {
        return `Total search terms in "all" and "one" fields cannot exceed 30 (currently ${totalTerms} terms)`;
    }
    
    return null;
};

/**
 * Counts the number of search terms in a string
 * A term is either a single word or a phrase in double quotes
 * @param {string} searchText - The search text to count terms in
 * @returns {number} - Number of terms found
 */
function countSearchTerms(searchText) {
    if (!searchText || typeof searchText !== 'string') {
        return 0;
    }
    
    const text = searchText.trim();
    if (text === '') {
        return 0;
    }
    
    let termCount = 0;
    let i = 0;
    
    while (i < text.length) {
        // Skip whitespace
        while (i < text.length && /\s/.test(text[i])) {
            i++;
        }
        
        if (i >= text.length) break;
        
        if (text[i] === '"') {
            // Found start of quoted phrase - count as one term
            i++; // skip opening quote
            let foundClosing = false;
            
            while (i < text.length) {
                if (text[i] === '"' && (i === 0 || text[i-1] !== '\\')) {
                    foundClosing = true;
                    i++; // skip closing quote
                    break;
                }
                i++;
            }
            
            // Only count if we found a proper closing quote
            if (foundClosing) {
                termCount++;
            }
        } else {
            // Found start of regular word - count as one term
            while (i < text.length && !/\s/.test(text[i])) {
                i++;
            }
            termCount++;
        }
    }
    
    return termCount;
}

/**
 * Validates the web search field for jobs
 * @param {string} searchValue - The search field value to validate
 * @returns {string|null} - Error message if validation fails, null if valid
 */
export const webSearchFieldValidation = (searchValue) => {
    
    //0. empty search
    if (!searchValue) {
        return null; // Allow empty search for now
    }
    
    //1. length check
    if (searchValue.length > 4000) {
        return `Search text is too long (${searchValue.length}/4000 characters)`;
    }

    //2. parentheses balance and nesting check
    const parentError = validateParentheses(searchValue);
    if (parentError) {
        return parentError;
    }

    //3. quotes balance check
    const quoteError = validateQuotes(searchValue);
    if (quoteError) {
        return quoteError;
    }

    //3b. smart quote check
    const smartQuoteError = validateSmartQuotes(searchValue);
    if (smartQuoteError) {
        return smartQuoteError;
    }

    //Strip quoted content so we don't inspect operators inside phrases
    const outsideQuotes = stripQuotedContent(searchValue);

    //4. NEAR operator check (reject NEAR outside quotes)
    const nearError = validateNearOperator(outsideQuotes);
    if (nearError) {
        return nearError;
    }

    //5. Logical operator case check (AND/OR/NOT must be uppercase)
    const operatorCaseError = validateOperatorCase(outsideQuotes);
    if (operatorCaseError) {
        return operatorCaseError;
    }
    
    // Future validation rules can be added here
    
    return null;
};


/**
 * Validates balanced and properly nested parentheses.
 */
function validateParentheses(text) {
    let stack = 0;

    for (let i = 0; i < text.length; i++) {
        const ch = text[i];

        if (ch === '(') {
            stack++;
        } else if (ch === ')') {
            stack--;
            if (stack < 0) {
                return `Unmatched closing parenthesis at position ${i + 1}`;
            }
        }
    }

    if (stack !== 0) {
        return 'Unbalanced parentheses: number of "(" and ")" does not match';
    }

    return null;
}


/**
 * Validates that double quotes (") are balanced (ignoring escaped quotes).
 */
function validateQuotes(text) {
    let inQuote = false;

    for (let i = 0; i < text.length; i++) {
        const ch = text[i];
        const prev = i > 0 ? text[i - 1] : null;

        if (ch === '"' && prev !== '\\') {
            inQuote = !inQuote;
        }
    }

    if (inQuote) {
        return 'Unbalanced quotes: missing closing " for a phrase';
    }

    return null;
}

/**
 * Rejects any non-straight double quote characters (smart/angled quotes).
 */
function validateSmartQuotes(text) {
    // Includes common smart double quotes, guillemets, corner quotes, and modifiers
    const disallowed = /[\u201C\u201D\u201E\u201F\u2033\u2036\u00AB\u00BB\u300C\u300D\u301D\u301E\u301F\u275D\u275E\u275F\u2760\u02DD\u02BA]/;

    if (disallowed.test(text)) {
        return 'Only straight double quotes (") are allowed. Please replace smart or angled quotes.';
    }

    return null;
}

/**
 * Returns a copy of the string where characters inside double quotes are
 * replaced by spaces, so we can safely scan operators outside phrases.
 */
function stripQuotedContent(text) {
    let result = '';
    let inQuote = false;

    for (let i = 0; i < text.length; i++) {
        const ch = text[i];
        const prev = i > 0 ? text[i - 1] : null;

        if (ch === '"' && prev !== '\\') {
            inQuote = !inQuote;
            result += ch; // keep the quote itself (or you can replace with space too)
        } else {
            result += inQuote ? ' ' : ch;
        }
    }

    return result;
}



/**
 * NEAR operator is not supported by Webz.io in this context.
 * Reject any NEAR or NEAR/x outside quotes.
 */
function validateNearOperator(text) {
    // Matches NEAR or NEAR/3 etc., case-insensitive
    const nearRegex = /\bNEAR(?:\s*\/\s*\d+)?\b/i;

    if (nearRegex.test(text)) {
        return 'NEAR operator is not supported. Please replace NEAR with explicit AND/OR logic.';
    }

    return null;
}


/**
 * Enforce uppercase AND / OR / NOT outside quotes.
 */
function validateOperatorCase(text) {
    // We look for standalone and/or/not tokens in non-uppercase form.
    // \b(and|or|not)\b with a negative check for fully uppercase.
    const regex = /\b(and|or|not)\b/gi;
    let match;

    while ((match = regex.exec(text)) !== null) {
        const token = match[0];
        if (token !== token.toUpperCase()) {
            return `Logical operators must be uppercase. Found "${token}", please use "${token.toUpperCase()}".`;
        }
    }

    return null;
}